import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import { overlayInnerStyle } from '../common-components/TooltipStyling';
import { ReactComponent as BizzIcon } from '../layout/icons/payment/bizz-big-less-white-space.svg';
import { ReactComponent as LicensePlateIcon } from '../layout/icons/payment/license-plate-big-less-white-space.svg';
import { ReactComponent as IsNotPaidIcon } from '../layout/icons/red-circle-exclamation-small.svg';
import { ReactComponent as GreenBadgeStatusIcon } from '../layout/icons/green-circle-exclamation-small.svg';
import { Transaction } from '../swagger/api';
import ContentStore from '../stores/ContentStore';
import dateFormatting from '../utils/dateFormatting';
import dateTimeFormatting from '../utils/dateTimeFormatting';
import decimalFormatting from '../utils/decimalFormatting';
import { isANPR, isBizz } from '../utils/productTypeUtilities';
import { Typography } from '../layout/components/Typography';
import { ColorScheme } from '../layout/colorScheme';
import LoadingSpinner from '../common-components/LoadingSpinner';
import {
  getInvoiceStatusDisplayInfo,
  getTransactionDisplayAmount,
  getTransactionLinkDisplayInfo,
  isPaidStatus
} from '../utils/invoiceStatusUtils';
import getDisplayTollChargerName from '../utils/getDisplayTollChargerName';
import getProductOrReferenceLabel from '../utils/getProductOrReferenceLabel';

const DescriptionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;

  .icon-container {
    min-width: 52px;
    display: flex;
    justify-content: flex-end;
    @media (max-width: 1200px) {
      display: none;
    }
  }

  .text-container {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
  }
`;

const StatusCell = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const StatusIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StatusBadge = styled.div<{ backgroundColor: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.backgroundColor};
  border-radius: 5px;
  padding: 8px 10px 6px;
  min-width: fit-content;
  cursor: pointer;

  /* Wrap to column when space is tight */
  @media (max-width: 1400px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px 10px;

    .status-text {
      text-align: left;
    }
  }
`;

const PriceCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const LinkCell = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const useTransactionsAndUsageColumns = (
  contentStore: ContentStore,
  isTableOnProductCard: boolean,
  showOutstandingOnly: boolean,
  isPrivateCustomer: boolean = false
) => {
  const { productItemsStore, productCardStore } = contentStore.rootStore.productStore;
  const { paymentStore, loadingStore } = contentStore.rootStore;
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);

  const showProductCardModalOnClick = (identifierId: string) => {
    if (!isTableOnProductCard) {
      loadingStore.enableGlobalSpinner();
      const productInStore = productItemsStore.findProductFromId(identifierId);
      if (productInStore) {
        productItemsStore.product = productInStore;
        productCardStore.openModal();
        loadingStore.disableGlobalSpinner();
      } else {
        productItemsStore.getProduct(identifierId).then(() => {
          productCardStore.openModal();
          loadingStore.disableGlobalSpinner();
        });
      }
    }
  };

  const dateAndTimeColumn = showOutstandingOnly
    ? {
        title: contentStore.cmsStore.getContent('TransactionsAndUsageTableDueDate')?.content,
        render: (row: Transaction) => (row.dueDate ? dateFormatting(row.dueDate) : '')
      }
    : {
        title: contentStore.cmsStore.getContent('DateAndTime')?.content,
        render: (row: Transaction) => (row.date ? dateTimeFormatting(row.date) : '')
      };

  const columns: ColumnsType<Transaction> = [
    dateAndTimeColumn,
    {
      title: (
        <Tooltip
          placement="top"
          title={contentStore.cmsStore.getContent('DescriptionInTableHeader')?.tooltip}
          color="#2d2d2d"
          overlayInnerStyle={overlayInnerStyle}
        >
          {contentStore.cmsStore.getContent('DescriptionInTableHeader')?.content}
        </Tooltip>
      ),
      render: (row: Transaction) => (
        <DescriptionCell>
          <Typography as="p" variant="contentSmallLink" align="left">
            {getDisplayTollChargerName(row.tollChargerName, (key) =>
              contentStore.cmsStore.getContent(key)
            )}
          </Typography>
          <Typography as="p" variant="contentSmall" color={ColorScheme.Grey6} align="left">
            {row.description ? contentStore.cmsStore.getContent(row.description)?.content : ''}
          </Typography>
        </DescriptionCell>
      )
    },
    {
      title: (
        <Tooltip
          placement="top"
          title={contentStore.cmsStore.getContent('ProductOrReference')?.tooltip}
          color="#2d2d2d"
          overlayInnerStyle={overlayInnerStyle}
        >
          {contentStore.cmsStore.getContent('ProductOrReference')?.content}
        </Tooltip>
      ),
      render: (row: Transaction) => {
        const currentReference = getProductOrReferenceLabel(row, productItemsStore);

        return (
          <ProductCell>
            <div className="icon-container">
              {isANPR(row.productType) ? (
                <LicensePlateIcon />
              ) : isBizz(row.productType) ? (
                <BizzIcon />
              ) : null}
            </div>
            <div className="text-container">
              <Typography
                as="button"
                align="left"
                variant="contentSmallLink"
                color={ColorScheme.BrobizzGreen}
                style={{
                  cursor: isTableOnProductCard ? 'auto' : 'pointer',
                  background: 'transparent',
                  border: 'none',
                  padding: 0
                }}
                onClick={() => {
                  if (row.identifierId && !isTableOnProductCard) {
                    showProductCardModalOnClick(row.identifierId);
                  }
                }}
              >
                {currentReference}
              </Typography>
              <Typography
                as="p"
                align="left"
                variant="contentSmall"
                color={ColorScheme.Grey6}
                style={{ cursor: 'auto' }}
              >
                {row.identifierNumber || ''}
              </Typography>
            </div>
          </ProductCell>
        );
      }
    },
    {
      title: contentStore.cmsStore.getContent('StatusInTableHeader')?.content,
      align: 'left' as const,
      onCell: (record: Transaction) => {
        const statusInfo = getInvoiceStatusDisplayInfo(
          {
            invoiceStatus: record.invoiceStatus as string,
            invoiceType: record.invoiceType,
            transactionType: record.transactionType,
            revenueOperationType: record.revenueOperationType,
            dueDate: record.dueDate
          },
          isPrivateCustomer ? 'private' : 'business-invoice'
        );
        return {
          className: statusInfo.showBadge ? 'status-column-with-badge' : 'status-column-plain',
          style: { justifyContent: 'flex-start' }
        };
      },
      onHeaderCell: () => ({ className: 'status-column-header' }),
      render: (row: Transaction) => {
        const statusInfo = getInvoiceStatusDisplayInfo(
          {
            invoiceStatus: row.invoiceStatus as string,
            invoiceType: row.invoiceType,
            transactionType: row.transactionType,
            revenueOperationType: row.revenueOperationType,
            dueDate: row.dueDate
          },
          isPrivateCustomer ? 'private' : 'business-invoice'
        );
        const statusText = contentStore.cmsStore.getContent(statusInfo.cmsKey)?.content;

        const isRedVariant =
          statusInfo.variant === 'missingPayment' || statusInfo.variant === 'paidOut';
        const textColor = isRedVariant
          ? ColorScheme.ErrorRed
          : statusInfo.variant === 'creditBalance' || statusInfo.variant === 'deposited'
          ? ColorScheme.BrobizzGreen
          : ColorScheme.Grey7;
        const backgroundColor = statusInfo.showBadge
          ? isRedVariant
            ? ColorScheme.OverdueBackground
            : ColorScheme.CreditBadgeBackground
          : 'transparent';

        return (
          <StatusCell>
            {statusInfo.showBadge ? (
              <Tooltip
                placement="bottomLeft"
                title={contentStore.cmsStore.getContent(statusInfo.cmsKey)?.tooltip}
                color="#2d2d2d"
                overlayInnerStyle={overlayInnerStyle}
                align={{ offset: [-4, 0] }}
              >
                <StatusBadge backgroundColor={backgroundColor}>
                  <StatusIconWrapper>
                    {isRedVariant ? <IsNotPaidIcon /> : <GreenBadgeStatusIcon />}
                  </StatusIconWrapper>
                  <Typography
                    as="p"
                    variant="contentSmall"
                    className="status-text"
                    align="left"
                    style={{ color: textColor, whiteSpace: 'nowrap' }}
                  >
                    {statusText}
                  </Typography>
                </StatusBadge>
              </Tooltip>
            ) : (
              <Typography as="p" variant="contentSmall" align="left" style={{ color: textColor }}>
                {statusText}
              </Typography>
            )}
          </StatusCell>
        );
      }
    },
    {
      title: contentStore.cmsStore.getContent('Price')?.content,
      align: 'right' as const,
      render: (row: Transaction) => {
        const statusInfo = getInvoiceStatusDisplayInfo(
          {
            invoiceStatus: row.invoiceStatus as string,
            invoiceType: row.invoiceType,
            transactionType: row.transactionType,
            revenueOperationType: row.revenueOperationType,
            dueDate: row.dueDate
          },
          isPrivateCustomer ? 'private' : 'business-invoice'
        );
        const isRedVariant =
          statusInfo.variant === 'missingPayment' || statusInfo.variant === 'paidOut';
        const priceColor = isRedVariant
          ? ColorScheme.ErrorRed
          : statusInfo.variant === 'creditBalance' || statusInfo.variant === 'deposited'
          ? ColorScheme.BrobizzGreen
          : ColorScheme.Grey7;

        const displayAmount = getTransactionDisplayAmount(
          {
            amount: row.amount,
            invoiceStatus: row.invoiceStatus as string,
            invoiceType: row.invoiceType,
            transactionType: row.transactionType,
            revenueOperationType: row.revenueOperationType,
            dueDate: row.dueDate
          },
          isPrivateCustomer ? 'private' : 'business-invoice'
        );

        return (
          <PriceCell>
            <Typography as="p" variant="contentSmall" align="right" style={{ color: priceColor }}>
              {decimalFormatting(displayAmount)}
              {` ${row.currency ?? ''}`}
            </Typography>
          </PriceCell>
        );
      }
    },
    {
      title: undefined,
      render: (row: Transaction) => {
        const statusInfo = getInvoiceStatusDisplayInfo(
          {
            invoiceStatus: row.invoiceStatus as string,
            invoiceType: row.invoiceType,
            transactionType: row.transactionType,
            revenueOperationType: row.revenueOperationType,
            dueDate: row.dueDate
          },
          isPrivateCustomer ? 'private' : 'business-invoice'
        );
        const hasInvoiceNumber = Boolean(row.invoiceNumber?.trim());
        const { showLink, cmsKey } = getTransactionLinkDisplayInfo(statusInfo, row.invoiceType, {
          invoiceStatus: row.invoiceStatus as string,
          transactionType: row.transactionType,
          context: isPrivateCustomer ? 'private' : 'business-invoice',
          hasInvoiceNumber
        });
        if (
          !row.invoiceId ||
          !showLink ||
          (!hasInvoiceNumber && !isPaidStatus(row.invoiceStatus as string))
        )
          return null;
        const rowKey = row.id ?? `${row.invoiceId}-${row.date}`;
        return (
          <LinkCell>
            {loadingInvoiceId === rowKey ? (
              <div
                style={{
                  height: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <Typography
                as="button"
                variant="contentSmallLink"
                align="right"
                color={ColorScheme.BrobizzGreen}
                style={{
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  padding: '1px 6px'
                }}
                onClick={() => {
                  setLoadingInvoiceId(rowKey);
                  paymentStore
                    .openInvoicePdfInNewTab(row.invoiceId!)
                    .then(() => setLoadingInvoiceId(null))
                    .catch(() => setLoadingInvoiceId(null));
                }}
              >
                {contentStore.cmsStore.getContent(cmsKey)?.content}
              </Typography>
            )}
          </LinkCell>
        );
      },
      align: 'right' as const
    }
  ];

  return columns;
};

export default useTransactionsAndUsageColumns;
